import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Layout, Row, Col, Button, List, Tag, Select,
  Skeleton, Pagination, Popover, Checkbox, Steps, Divider,
} from 'antd';
import SVG from 'react-inlinesvg';
import history from '../modules/history';
import ReactTimeAgo from 'react-time-ago';
import JavascriptTimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { LoadingSpinner } from './LoadingSpinner';
import { getLeads, deleteLeads } from '../actions/lead';
import { getPages } from '../actions/page';
import { EmptyState } from './EmptyState';
import { analytics } from '../modules/analytics';

import {
  SyncOutlined,
  CaretRightOutlined,
  HddOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  AimOutlined,
  EnvironmentOutlined,
  DeleteOutlined,
  SmileOutlined,
  CloudUploadOutlined,
  UserAddOutlined,
} from '@ant-design/icons';

JavascriptTimeAgo.locale(en);


export class LeadMap extends React.Component {

  constructor(props) {
    super(props);
    this.state.currentPageUrlsafe = props.navPageUrlsafe;
    this.state.currentVariantUrlsafe = props.navVariantUrlsafe;
  }

  state = {
    currentPageUrlsafe: 'all',
    currentVariantUrlsafe: 'all',

    currentPage: 1,
    pageSize: 50,

    selected: [],
  };

  static propTypes = {
    navPageUrlsafe: PropTypes.string,
    navVariantUrlsafe: PropTypes.string,

    lead: PropTypes.shape({
      leads: PropTypes.array,
      status: PropTypes.string,
    }).isRequired,

    page: PropTypes.shape({
      pages: PropTypes.array.isRequired,
      status: PropTypes.string,
    }).isRequired,

    dispatch: PropTypes.func.isRequired,
    networkError: PropTypes.bool.isRequired,
  };

  componentDidMount() {
    analytics.trackView('/leads');
    if (this.props.lead.status === 'init') {
      this.props.dispatch(getLeads());
    }
    if (this.props.page.status === 'init') {
      this.props.dispatch(getPages());
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.networkError && !this.props.networkError) {
      this.setState({ currentPage: 1 });
      this.props.dispatch(getPages());
      this.props.dispatch(getLeads());
    }

    if (prevProps.navPageUrlsafe !== this.props.navPageUrlsafe) {
      this.setState({
        currentPageUrlsafe: this.props.navPageUrlsafe,
        currentPage: 1,
        selected: [],
      });
    }
    if (prevProps.navVariantUrlsafe !== this.props.navVariantUrlsafe) {
      this.setState({
        currentVariantUrlsafe: this.props.navVariantUrlsafe,
        currentPage: 1,
        selected: [],
      });
    }
  }

  globalSelected = () => {
    const {
      leads = [],
      status: leadStatus = 'loading',
    } = this.props.lead;
    const { paginatedLeads } = this.filtered(leads);

    const selectedCount = this.state.selected.length;
    const isIndeterminate = selectedCount > 0 && selectedCount !== paginatedLeads.length;

    if (isIndeterminate) {
      this.setState({selected: []});
    }
    else {
      if (selectedCount === 0) {
        const urlsafes = paginatedLeads.map(l => l.urlsafe);
        this.setState({selected: urlsafes});
      }
      else {
        this.setState({selected: []});
      }
    }
  };

  renderListCaptions = (paginatedLeadsLength) => {

    const selectedCount = this.state.selected.length;
    const isIndeterminate = selectedCount > 0 && selectedCount !== paginatedLeadsLength;

    return (
      <div className="row text-secondary small">
        <div className="col-8 col-sm-9 col-md-3 col-lg-4">
          <Checkbox
            className="text-secondary"
            checked={selectedCount > 0}
            indeterminate={isIndeterminate}
            onChange={this.globalSelected}
          >Name</Checkbox>
        </div>
        <div className="col-4 col-sm-3 col-md-3 col-lg-2 text-right text-md-left">
          <Popover
            title={null} placement="bottomLeft"
            content="Location info estimated from form submissions are approximate."
          >
            Origin
          </Popover>
        </div>
        <div className="d-none d-md-block col-md-6 text-left">
          <div style={{ paddingLeft: 80 }}>
            Data
          </div>
        </div>
      </div>
    );
  };



  shortenLatLong = latLong => {
    let latl = null;
    let latf = null;
    let longf = null;
    let longl = null;

    try {
      const [lat, long] = latLong.split(',');
      [latf, latl] = lat.split('.');
      [longf, longl] = long.split('.');
    }
    catch (err) {
      return 'None';
    }

    latl = latl.substring(0, 2);
    longl = longl.substring(0, 2);

    const newLat = [latf, latl].join('.');
    const newLong = [longf, longl].join('.');
    const newLatLong = [newLat, newLong].join(',');
    return newLatLong;
  };


  itemSelected = (urlsafe) => {
    const nextSelected = (this.state.selected.includes(urlsafe)) ?
      this.state.selected.filter(e => e !== urlsafe) :
      [...this.state.selected, urlsafe];

    this.setState({selected: nextSelected});
  };

  renderLeadItem = item => {
    const { ip, name, type, data = null, created, city, country, cityLatLong } = item;

    let displayData = null;
    if (data) {
      displayData = data.map((d, i) => (
        <div key={i} className="d-flex">
          <div style={{ minWidth: 80 }} className="text-muted small text-left text-md-right pr-0 pr-md-1">
            {d.name}
          </div>
          <div className="small">
            {d.value}
          </div>
        </div>
      ));
    }

    const shortenedLatLong = this.shortenLatLong(cityLatLong);

    const isSelected = this.state.selected.includes(item.urlsafe);

    return (
      <List.Item className={cx("lead__row row align-items-start", { selected: isSelected })}>
        <div className="col-8 col-sm-9 col-md-3 col-lg-4">
          <Checkbox
            className={cx({"text-primary": isSelected})}
            onChange={() => this.itemSelected(item.urlsafe)}
            checked={isSelected}
          >{name}</Checkbox>
          <div className="text-muted"><ReactTimeAgo className="text-muted small" date={created/1000} /></div>
        </div>
        <div className="col-4 col-sm-3 col-md-3 col-lg-2 text-left text-secondary">
          <div className="d-flex flex-row-reverse flex-md-row align-items-center small"><HddOutlined className="text-muted ml-1 ml-md-0 mr-0 mr-md-1" />{ip}</div>
          <div className="d-flex flex-row-reverse flex-md-row align-items-center small"><EnvironmentOutlined className="text-muted ml-1 ml-md-0 mr-0 mr-md-1" />
            <div style={{ textTransform: 'capitalize'}}>{city}, {country}</div>
          </div>
          <div className="d-flex flex-row-reverse flex-md-row align-items-center small"><AimOutlined className="text-muted ml-1 ml-md-0 mr-0 mr-md-1" />{shortenedLatLong}</div>
        </div>
        <div className="col-md-6 col-lg-6">
          {displayData}
        </div>
      </List.Item>
    );
  };

  onSelectPage = (value) => {
    if (this.state.currentPageUrlsafe !== value) {
      this.setState({
        currentPageUrlsafe: value,
        currentVariantUrlsafe: 'all',
        currentPage: 1,
        selected: [],
      });
    }
  };

  onSelectVariant = (value) => {
    if (this.state.currentVariantUrlsafe !== value) {
      this.setState({
        currentVariantUrlsafe: value,
        currentPage: 1,
        selected: [],
      });
    }
  };

  filterByPageVariants = (lead) => {

    if (this.state.currentPageUrlsafe === 'all') {
      return true;
    }
    if (this.state.currentVariantUrlsafe === 'all') {
      const { pages } = this.props.page;
      const relevantPage = pages.find(p => p.urlsafe === this.state.currentPageUrlsafe);
      if (typeof relevantPage === 'undefined') {
        return false;
      }
      const variantIndex = relevantPage.variants.findIndex(v => v.urlsafe === lead.variantUrlsafe);
      return variantIndex !== -1;
    }

    return lead.variantUrlsafe === this.state.currentVariantUrlsafe;
  };

  renderHeaderBar = () => {
    const { pages, status } = this.props.page;

    const pageOptions = [
      <Select.Option key="all" value="all">Leads from all pages</Select.Option>,
      pages.map((p, index) =>
        <Select.Option key={p.urlsafe} value={p.urlsafe}>{p.name ? p.name : `Unnamed Page #${index+1}`}</Select.Option>
      ),
    ];
    const pageSelect = (
      <Select onChange={this.onSelectPage} value={this.state.currentPageUrlsafe}>
        {pageOptions}
      </Select>
    );

    let variantSelect = null;
    let selectorSeparator = null;
    if (this.state.currentPageUrlsafe !== 'all') {
      const page = pages.find(p => p.urlsafe === this.state.currentPageUrlsafe);
      const variantOptions = [
        <Select.Option key="all" value="all">All variants</Select.Option>,
        (typeof page === 'undefined') ? [] : page.variants.map((v, index) =>
          <Select.Option key={v.urlsafe} value={v.urlsafe}>{v.name ? v.name : `Unnamed Variant #${index + 1}`}</Select.Option>
        ),
      ];
      variantSelect = (
        <Select onChange={this.onSelectVariant} value={this.state.currentVariantUrlsafe}>
          {variantOptions}
        </Select>
      );
      selectorSeparator = <CaretRightOutlined className="text-muted mx-1" />;
    }

    let selectNode = null;
    if (status === 'loaded') {
      selectNode = (
        <div className="pickerHolder d-flex justify-content-start align-items-center">
          {pageSelect}
          {selectorSeparator}
          {variantSelect}
        </div>
      );
    }

    const isSelected = this.state.selected.length > 0;

    const refreshBtn = (
      <Button
        type="link" className="px-0"
        icon={<SyncOutlined />} onClick={() => {
          this.props.dispatch(getLeads());
          this.setState({ currentPage: 1, selected: [] });
        }}
      >
        Refresh
      </Button>
    );

    const deleteBtn = (
      <Button
        type="danger"
        icon={<DeleteOutlined />} onClick={() => {
          this.props.dispatch(deleteLeads(this.state.selected));
          this.setState({ selected: [] });
        }}
      >
        Delete
      </Button>
    );

    return (
      <div className="d-flex justify-content-between mb-4 align-items-center">
        {selectNode}
        <div>
          { isSelected ? (
            deleteBtn
          ) : (refreshBtn) }
        </div>
      </div>
    );
  };

  filtered = (leads) => {
    const filteredLeads = (
      leads.filter(this.filterByPageVariants)
        .sort((a, b) => (b.created - a.created))
    );

    const paginateFloor = ((this.state.currentPage - 1) * this.state.pageSize)
    const paginateCeiling = (this.state.currentPage * this.state.pageSize)
    const paginatedLeads = filteredLeads.slice(paginateFloor, paginateCeiling);

    return {
      filteredLeads,
      paginatedLeads,
    }
  };

  renderEmptyState = () => (
    <EmptyState
      title="No leads yet"
      subtitle="But there's potential. Leads generated from your pages and variants will appear here."
      graphics={<SVG src={require('assets/media/lead/lead-empty.svg')} />}
      btnLabel="Refresh"
      btnIcon={<SyncOutlined />}
      btnCallback={() => {
        this.props.dispatch(getLeads());
        this.setState({ currentPage: 1 });
      }}
    >
      <div className="mt-2 mb-4 text-dark lead">Lead-generation milestones</div>
      <Steps size="small">
        <Steps.Step status="finish" icon={<CloudUploadOutlined />} title="Publish" description="Design and publish your pages." />
        <Steps.Step status="finish" icon={<FileDoneOutlined />} title="Promote" description="Run campaigns or spread the word to drive traffic to your pages." />
        <Steps.Step status="finish" icon={<UserAddOutlined />} title="Generate" description="Website visitors who sign up on your pages show up here." />
        <Steps.Step status="finish" icon={<SmileOutlined />} title="Close" description="Follow up with your leads and close the deal. Cha-ching." />
      </Steps>
    </EmptyState>
  );

  render() {
    const {
      leads = [],
      status: leadStatus = 'loading',
    } = this.props.lead;
    const isLoading = (leadStatus === 'loading');
    const isEmpty = leads.length === 0;

    const { filteredLeads, paginatedLeads } = this.filtered(leads);

    return (
      <div className="app__lead">
        <Helmet>
          <title>{isLoading ? 'Loading Leads' : 'Leads'}</title>
        </Helmet>

        { isEmpty && !isLoading ? this.renderEmptyState() : null }

        { isEmpty ? null : this.renderHeaderBar() }

        { isLoading ? <LoadingSpinner /> : null }

        { leads.length > 0 && filteredLeads.length === 0 ? (
          <div className="shadow-sm bg-white rounded py-5 px-3 text-center text-secondary">
            No leads generated yet from currently selected page and variants.
          </div>
        ) : null }

        { filteredLeads.length > 0 ? (
          <div className="mb-5">

            <div className="text-right">
              <Pagination
                onChange={(page, pageSize) => {
                  this.setState({currentPage: page, pageSize: pageSize, selected: []});
                }}
                size="small"
                current={this.state.currentPage}
                pageSize={this.state.pageSize}
                hideOnSinglePage={true}
                total={filteredLeads.length}
              />
            </div>

            <div className="container-fluid mt-2 mb-1">
              { this.renderListCaptions(paginatedLeads.length) }
            </div>

            <div className="container-fluid bg-white shadow-sm rounded">
              <List
                bordered={false}
                className="lead-list"
                dataSource={paginatedLeads}
                renderItem={this.renderLeadItem}
              />
            </div>

            <div className="text-right mt-3">
              <Pagination
                onChange={(page, pageSize) => {
                  this.setState({currentPage: page, pageSize: pageSize, selected: []});
                }}
                current={this.state.currentPage}
                pageSize={this.state.pageSize}
                hideOnSinglePage={true}
                total={filteredLeads.length}
              />
            </div>
          </div>

        ) : null}

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/presentation/LeadMap.jsx